import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";
const CORPORATE_IDENTITY_MODE = import.meta.env.VITE_OIDC_ENABLED === "true";
let inMemoryAccessToken: string | null = null;

interface AccessTokenResponse {
  access: string;
  refresh?: string;
}

interface BootstrapContextResponse {
  active_organization?: {
    id?: string;
    org_type?: string;
  } | null;
  active_workspace?: {
    id?: string;
  } | null;
  permissions?: {
    security_role?: string;
  };
}

export type TenantContextSnapshot = Readonly<{
  organization: string | null;
  workspace: string | null;
}>;

type TenantContextRequestConfig = AxiosRequestConfig & {
  tenantContextSnapshot: TenantContextSnapshot;
};

type InternalTenantContextRequestConfig = InternalAxiosRequestConfig & {
  tenantContextSnapshot?: TenantContextSnapshot;
};

/**
 * Bind a request to the same immutable tenant snapshot used by its cache key.
 * The shared interceptor treats this explicit context as authoritative instead
 * of consulting mutable browser storage again when Axios dispatches.
 */
export function withTenantContextSnapshot(
  tenantContextSnapshot: TenantContextSnapshot,
  config: AxiosRequestConfig = {},
): TenantContextRequestConfig {
  return { ...config, tenantContextSnapshot };
}

export function getAccessToken(): string | null {
  return CORPORATE_IDENTITY_MODE
    ? inMemoryAccessToken
    : localStorage.getItem("access_token");
}

export function setAccessToken(token: string): void {
  inMemoryAccessToken = token;
  if (!CORPORATE_IDENTITY_MODE) localStorage.setItem("access_token", token);
}

export function clearAccessToken(): void {
  inMemoryAccessToken = null;
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("oidc_id_token");
  sessionStorage.removeItem("oidc_id_token");
}

function unwrapAccessToken(
  payload: { data: AccessTokenResponse } | AccessTokenResponse,
): AccessTokenResponse {
  return "data" in payload && "status" in (payload as Record<string, unknown>)
    ? (payload as { data: AccessTokenResponse }).data
    : (payload as AccessTokenResponse);
}

export async function refreshAccessToken(): Promise<string> {
  const legacyRefresh = CORPORATE_IDENTITY_MODE
    ? undefined
    : (localStorage.getItem("refresh_token") ?? undefined);
  const { data } = await axios.post<
    { data: AccessTokenResponse } | AccessTokenResponse
  >(
    `${API_BASE_URL}/auth/token/refresh/`,
    legacyRefresh ? { refresh: legacyRefresh } : {},
    { withCredentials: true },
  );
  const tokens = unwrapAccessToken(data);
  setAccessToken(tokens.access);
  if (!CORPORATE_IDENTITY_MODE && tokens.refresh) {
    localStorage.setItem("refresh_token", tokens.refresh);
  }
  return tokens.access;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/** Attach JWT bearer token and org/workspace context to every request */
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // While the stored organization is being replaced (SEC-D41), a read waits
  // for the adopted context and a write is refused: it was meant for the
  // organization the person is still looking at.
  const recovery = organizationRecovery;
  if (!(config as RecoveryRequestConfig)._organizationRecovery) {
    if (
      (recovery || pendingOrganizationReconciliations > 0) &&
      !isRetryableAfterRecovery(config.method)
    ) {
      throw refusedWriteDuringRecovery(config);
    }
    if (recovery) await recovery.catch(() => undefined);
  }

  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Send active organization/workspace so the backend middleware resolves
  // the correct tenant context instead of falling back to `.first()`. Runtime
  // reads carry an explicit snapshot; other callers retain the established
  // localStorage behavior.
  const snapshot = (config as InternalTenantContextRequestConfig)
    .tenantContextSnapshot;
  const orgId = snapshot
    ? snapshot.organization
    : localStorage.getItem("active_organization_id");
  const wsId = snapshot
    ? snapshot.workspace
    : localStorage.getItem("active_workspace_id");
  if (config.headers) {
    if (orgId) config.headers.set("X-Organization-Id", orgId);
    else if (snapshot) config.headers.delete("X-Organization-Id");
    if (wsId) config.headers.set("X-Workspace-Id", wsId);
    else if (snapshot) config.headers.delete("X-Workspace-Id");
  }

  return config;
});

let isRefreshing = false;
let organizationRecovery: Promise<void> | null = null;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

function responseErrorCode(error: AxiosError): string | undefined {
  const body = error.response?.data as
    | { errors?: Array<{ code?: string }> }
    | undefined;
  return body?.errors?.[0]?.code;
}

/** Only these may be resent after adopting another organization (SEC-D41). */
const ORGANIZATION_RETRY_METHODS = new Set(["get", "head", "options"]);

type RecoveryRequestConfig = InternalAxiosRequestConfig & {
  /** The bootstrap that the recovery itself issues. */
  _organizationRecovery?: boolean;
};

function isRetryableAfterRecovery(method: string | undefined): boolean {
  return ORGANIZATION_RETRY_METHODS.has((method ?? "get").toLowerCase());
}

/** Keep a write on the tenant it was first sent to when it is retried. */
function pinWriteTenant(config: InternalTenantContextRequestConfig): void {
  if (isRetryableAfterRecovery(config.method) || config.tenantContextSnapshot) {
    return;
  }
  const organization = config.headers?.get("X-Organization-Id");
  const workspace = config.headers?.get("X-Workspace-Id");
  config.tenantContextSnapshot = {
    organization: typeof organization === "string" ? organization : null,
    workspace: typeof workspace === "string" ? workspace : null,
  };
}

/** Dispatched on `window` once per recovery, after the adopted context is stored. */
export const ORGANIZATION_CONTEXT_RECOVERED_EVENT =
  "sentinels:organization-context-recovered";

const ORGANIZATION_RECOVERY_TIMEOUT_MS = 15_000;
/** Fail open: never hold writes longer than this after a recovery. */
const ORGANIZATION_RECONCILIATION_CAP_MS = 25_000;
let organizationReconcilerMounted = false;
let pendingOrganizationReconciliations = 0;
let organizationReconciliationCap: ReturnType<typeof setTimeout> | undefined;

function releaseOrganizationWrites(): void {
  pendingOrganizationReconciliations = 0;
  clearTimeout(organizationReconciliationCap);
}

function holdOrganizationWrites(): void {
  if (!organizationReconcilerMounted) return;
  pendingOrganizationReconciliations += 1;
  clearTimeout(organizationReconciliationCap);
  organizationReconciliationCap = setTimeout(
    releaseOrganizationWrites,
    ORGANIZATION_RECONCILIATION_CAP_MS,
  );
}

/** The UI follows recoveries; writes wait for it to reconcile (SEC-D41). */
export function setOrganizationReconcilerMounted(mounted: boolean): void {
  organizationReconcilerMounted = mounted;
  if (!mounted) releaseOrganizationWrites();
}

/** The UI now shows the adopted organization: writes may go out again. */
export function markOrganizationContextReconciled(): void {
  pendingOrganizationReconciliations = Math.max(
    0,
    pendingOrganizationReconciliations - 1,
  );
  if (pendingOrganizationReconciliations === 0) releaseOrganizationWrites();
}

let organizationWriteRefusedMessage = (): string =>
  "Nothing was saved: your access to that organization changed. Check the active organization and repeat the action.";

/** Let the UI give a refused write its translated message. */
export function setOrganizationWriteRefusedMessage(
  message: () => string,
): void {
  organizationWriteRefusedMessage = message;
}

function organizationWriteRefusedBody(body: unknown) {
  return {
    ...(body !== null && typeof body === "object" ? body : {}),
    status: "error",
    data: null,
    errors: [
      {
        code: "organization_context_changed",
        message: organizationWriteRefusedMessage(),
      },
    ],
  };
}

/** Report a write that was not resent into the adopted organization. */
function refuseCrossOrganizationWrite(error: AxiosError): AxiosError {
  if (error.response) {
    error.response.data = organizationWriteRefusedBody(error.response.data);
  }
  return error;
}

/** Report a write issued while the organization was being replaced: never sent. */
function refusedWriteDuringRecovery(
  config: InternalAxiosRequestConfig,
): AxiosError {
  return new AxiosError(
    organizationWriteRefusedMessage(),
    "ERR_ORGANIZATION_CONTEXT_CHANGED",
    config,
    undefined,
    {
      data: organizationWriteRefusedBody(null),
      status: 403,
      statusText: "Forbidden",
      headers: {},
      config,
    },
  );
}

function unwrapBootstrapContext(
  payload:
    | { status?: string; data: BootstrapContextResponse }
    | BootstrapContextResponse,
): BootstrapContextResponse {
  return "data" in payload
    ? (payload as { data: BootstrapContextResponse }).data
    : (payload as BootstrapContextResponse);
}

async function recoverOrganizationContext(): Promise<void> {
  // Bootstrap is explicitly exempt from stale-organization rejection. Clear
  // both levels first so its request interceptor cannot resend the bad pair.
  const previousOrganization = localStorage.getItem("active_organization_id");
  const previousWorkspace = localStorage.getItem("active_workspace_id");
  localStorage.removeItem("active_organization_id");
  localStorage.removeItem("active_workspace_id");

  let data:
    | { status?: string; data: BootstrapContextResponse }
    | BootstrapContextResponse;
  try {
    ({ data } = await api.get("/accounts/session/bootstrap/", {
      _organizationRecovery: true,
      // Every new read waits on this request; never let it hang.
      timeout: ORGANIZATION_RECOVERY_TIMEOUT_MS,
    } as AxiosRequestConfig));
  } catch (error) {
    // Never leave later requests headerless: the backend would fall back to
    // the first membership instead of refusing (#491).
    if (
      previousOrganization &&
      !localStorage.getItem("active_organization_id")
    ) {
      localStorage.setItem("active_organization_id", previousOrganization);
    }
    if (previousWorkspace && !localStorage.getItem("active_workspace_id")) {
      localStorage.setItem("active_workspace_id", previousWorkspace);
    }
    throw error;
  }
  const bootstrap = unwrapBootstrapContext(data);
  const platformOwnerContext =
    bootstrap.permissions?.security_role === "platform_admin" &&
    bootstrap.active_organization?.org_type === "platform_owner";

  // Match AuthProvider's storage contract: the technical platform-owner
  // anchor means cross-client scope and must not become a client header.
  if (!platformOwnerContext && bootstrap.active_organization?.id) {
    localStorage.setItem(
      "active_organization_id",
      bootstrap.active_organization.id,
    );
  }
  if (!platformOwnerContext && bootstrap.active_workspace?.id) {
    localStorage.setItem("active_workspace_id", bootstrap.active_workspace.id);
  }
  // Until the UI has reloaded the session and dropped the old reads, the
  // person still sees the old organization: keep refusing writes.
  holdOrganizationWrites();
  window.dispatchEvent(new Event(ORGANIZATION_CONTEXT_RECOVERED_EVENT));
}

function recoverOrganizationContextOnce(): Promise<void> {
  if (!organizationRecovery) {
    organizationRecovery = recoverOrganizationContext().finally(() => {
      organizationRecovery = null;
    });
  }
  return organizationRecovery;
}

/** Normalize paginated DRF responses so components can do `data?.data ?? []`.
 *
 * The backend wraps list endpoints in two layers:
 *   { status, data: { count, next, previous, results: [...] }, meta }
 *
 * Many components expect `data.data` to be the array directly.
 * This interceptor hoists `results` up:
 *   { status, data: [...], meta: { ..., total: count, next, previous } }
 *
 * Non-paginated responses (where data is already an array or a single object
 * without a `results` key) are passed through unchanged.
 */
api.interceptors.response.use((response) => {
  const body = response.data as {
    status?: string;
    data?: {
      count?: number;
      results?: unknown[];
      next?: string | null;
      previous?: string | null;
      truncated?: boolean;
      limit?: number;
    };
    meta?: Record<string, unknown>;
  };

  if (
    body?.status === "success" &&
    body?.data !== null &&
    typeof body?.data === "object" &&
    !Array.isArray(body.data) &&
    "results" in body.data &&
    Array.isArray(body.data.results)
  ) {
    response.data = {
      ...body,
      data: body.data.results,
      meta: {
        ...(body.meta ?? {}),
        ...(typeof body.data.count === "number"
          ? { total: body.data.count }
          : body.data.truncated === true
            ? {}
            : { total: body.data.results.length }),
        next: body.data.next ?? null,
        previous: body.data.previous ?? null,
        ...(typeof body.data.truncated === "boolean"
          ? { truncated: body.data.truncated }
          : {}),
        ...(typeof body.data.limit === "number"
          ? { limit: body.data.limit }
          : {}),
      },
    };
  }

  return response;
});

/** Recover stale tenant context, or handle 401 token refresh.
 *
 * A 403 remains final except for the backend's exact
 * `organization_access_denied` code: that means the persisted tenant header is
 * unusable, and the bootstrap route exists to rediscover a valid context.
 * Authorization denials never trigger this path. Both recovery channels mark
 * their own one retry on the original request, preventing loops (#419, #506).
 * Only reads are resent: a write named the organization it was meant for, and
 * resending it would create it in the adopted one (#491, SEC-D41).
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest =
      error.config as InternalTenantContextRequestConfig & {
        _retry?: boolean;
        _organizationRetry?: boolean;
      };

    if (
      error.response?.status === 403 &&
      responseErrorCode(error) === "organization_access_denied"
    ) {
      if (originalRequest._organizationRetry) {
        return Promise.reject(error);
      }
      originalRequest._organizationRetry = true;

      try {
        await recoverOrganizationContextOnce();
        if (!isRetryableAfterRecovery(originalRequest.method)) {
          return Promise.reject(refuseCrossOrganizationWrite(error));
        }
        // Axios retries the same config object. Remove the rejected values so
        // the request interceptor can inject the newly bootstrapped context —
        // or leave both absent for platform-wide scope.
        originalRequest.headers?.delete("X-Organization-Id");
        originalRequest.headers?.delete("X-Workspace-Id");
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401) {
      // The retry re-reads stored context; a write keeps the tenant it named.
      pinWriteTenant(originalRequest);
      if (originalRequest._retry) {
        clearAccessToken();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest._retry = true;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!CORPORATE_IDENTITY_MODE && !refreshToken) {
        processQueue(error, null);
        isRefreshing = false;
        clearAccessToken();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const access = await refreshAccessToken();
        processQueue(null, access);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
